import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Landmark, Send, Loader2, CreditCard, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const copy = (text, what) => {
  navigator.clipboard?.writeText(text).then(() => toast.success(`${what} copiato`)).catch(() => toast.error("Copia non riuscita"));
};

const Row = ({ label, value, testid, copyable, mono }) => (
  <div className="flex items-start justify-between gap-3 py-2">
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p data-testid={testid} className={`text-sm font-semibold text-slate-900 ${mono ? "break-all font-mono-code" : "break-words"}`}>{value}</p>
    </div>
    {copyable && (
      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-[#166534]" onClick={() => copy(value, label)} data-testid={`${testid}-copy`}>
        <Copy className="h-4 w-4" />
      </Button>
    )}
  </div>
);

const reportPayment = async (body, onSuccess, setBusy, msg) => {
  setBusy(true);
  try {
    const { data: res } = await api.post("/payments/bonifico", body);
    toast.success(msg);
    onSuccess?.(res);
    return true;
  } catch (err) {
    toast.error(apiError(err.response?.data?.detail) || "Segnalazione non riuscita");
    return false;
  } finally { setBusy(false); }
};

export function SumUpPanel({ config, tipo, amount, causale, causaleBonifico, donorName, donorEmail, donorCf, disabled, onSuccess }) {
  const [rif, setRif] = useState("");
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);
  const key = tipo === "tesseramento" ? "link_quota" : tipo === "donazione" ? "link_donazione" : "link_altro";
  const link = config?.sumup?.[key];
  if (!link) {
    return <p data-testid="sumup-not-configured" className="rounded-lg border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">Pagamento con SumUp non ancora attivo per questa voce. Usa il bonifico bancario.</p>;
  }
  const imp = Number(amount || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const submit = async () => {
    const ok = await reportPayment({
      tipo, amount, causale, donor_name: donorName, donor_email: donorEmail || null, donor_cf: donorCf,
      riferimento: rif, metodo: "sumup",
    }, onSuccess, setBusy, "Pagamento segnalato: verrà confermato dagli amministratori");
    if (ok) { setRif(""); setOpened(false); }
  };
  return (
    <div className="space-y-4" data-testid="sumup-panel">
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 divide-y divide-slate-200">
        <Row label="Importo da pagare" value={`€ ${imp}`} testid="sumup-importo" mono />
        <Row label="Descrizione da indicare" value={causaleBonifico} testid="sumup-causale" copyable />
      </div>
      <Button asChild disabled={disabled} className="w-full bg-[#D97706] hover:bg-[#b45309]">
        <a href={link} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (disabled) { e.preventDefault(); return; } setOpened(true); }} data-testid="sumup-pay-link"
          aria-disabled={disabled} tabIndex={disabled ? -1 : 0}
          className={disabled ? "pointer-events-none opacity-50" : ""}>
          <CreditCard className="mr-2 h-4 w-4" /> Paga con SumUp <ExternalLink className="ml-2 h-3.5 w-3.5" />
        </a>
      </Button>
      <p className="text-xs text-slate-500">Si apre la pagina sicura SumUp (carta, Apple Pay, Google Pay). Dopo il pagamento torna qui e segnalalo.</p>
      {opened && (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="sumup-report">
          <div className="space-y-1">
            <Label className="text-xs">Codice transazione SumUp (facoltativo)</Label>
            <Input value={rif} onChange={(e) => setRif(e.target.value)} placeholder="Dalla email di conferma SumUp" data-testid="sumup-riferimento" />
          </div>
          <Button type="button" onClick={submit} disabled={disabled || busy} className="w-full bg-[#14382C] hover:bg-[#0E281F]" data-testid="sumup-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Ho pagato con SumUp
          </Button>
        </div>
      )}
    </div>
  );
}

export function BonificoPanel({ config, tipo, amount, causale, causaleBonifico, donorName, donorEmail, donorCf, disabled, onSuccess }) {
  const [data, setData] = useState("");
  const [rif, setRif] = useState("");
  const [busy, setBusy] = useState(false);
  const bank = config?.bonifico;
  if (!bank) { return null; }
  const imp = Number(amount || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const submit = async () => {
    const ok = await reportPayment({
      tipo, amount, causale, donor_name: donorName, donor_email: donorEmail || null, donor_cf: donorCf,
      data_bonifico: data, riferimento: rif, metodo: "bonifico",
    }, onSuccess, setBusy, "Bonifico segnalato: verrà confermato alla ricezione");
    if (ok) { setData(""); setRif(""); }
  };

  return (
    <div className="space-y-4" data-testid="bonifico-panel">
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 divide-y divide-slate-200">
        <Row label="Intestatario" value={bank.intestatario} testid="bonifico-intestatario" copyable />
        <Row label="IBAN" value={bank.iban} testid="bonifico-iban" copyable mono />
        <Row label="Importo" value={`€ ${imp}`} testid="bonifico-importo" mono />
        <Row label="Causale" value={causaleBonifico} testid="bonifico-causale" copyable />
      </div>
      <p className="flex items-start gap-2 text-xs text-slate-500"><Landmark className="mt-0.5 h-4 w-4 shrink-0 text-[#166534]" /> Esegui il bonifico dalla tua banca con questi dati, poi segnalalo qui: verrà confermato dagli amministratori alla ricezione.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label className="text-xs">Data del bonifico</Label>
          <Input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="bonifico-data" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">CRO / TRN (facoltativo)</Label>
          <Input value={rif} onChange={(e) => setRif(e.target.value)} placeholder="Codice riferimento" data-testid="bonifico-riferimento" />
        </div>
      </div>
      <Button type="button" onClick={submit} disabled={disabled || busy} className="w-full bg-[#14382C] hover:bg-[#0E281F]" data-testid="bonifico-submit">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Ho effettuato il bonifico
      </Button>
    </div>
  );
}

export function MetodoSwitch({ value, onChange }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1" data-testid="metodo-switch">
      {[["bonifico", "Bonifico bancario"], ["sumup", "Carta con SumUp"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`metodo-${k}`}
          className={`rounded-md py-2 text-xs font-semibold transition-colors ${value === k ? "bg-white text-[#14382C] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
      ))}
    </div>
  );
}
